/**
 * CTA styling.
 *
 * Note what is NOT here: the primary conversion button itself.
 *
 * On the live site that button is not a Webflow element at all — it is a raw inline-styled
 * `<button>` inside a `w-embed w-script`, wired to `handleConversionClick(loc, pos, utmContent)`,
 * with its DOM id doubling as the analytics position. Their Mixpanel and PostHog funnels bind
 * to exactly that shape. We reproduce it verbatim as an `IREmbed` (see `blocks/landing/cta.ts`)
 * rather than styling a nicer equivalent, because a nicer equivalent would silently stop
 * reporting conversions.
 *
 * What lives here is the surrounding layout, plus the secondary/ghost link that has no
 * analytics contract.
 */

import { colour, radiusScale, space } from '../tokens.js';
import { BOX_RESET, font, TEXT_RESET, type StyleDef } from '../styleDef.js';

/**
 * Secondary action. Deliberately not pink: the brand rule is that pink appears once per view,
 * and that once is the primary CTA.
 */
export const lpCtaSecondary: StyleDef = {
  name: 'lp-cta-secondary',
  base: {
    ...TEXT_RESET,
    ...font('body'),
    'font-weight': '600',
    color: colour.indigo,
    display: 'inline-flex',
    'align-items': 'center',
    'padding-top': space[3],
    'padding-bottom': space[3],
    'padding-left': space[5],
    'padding-right': space[5],
    'background-color': 'transparent',
    'border-top-width': '1.5px',
    'border-top-style': 'solid',
    'border-top-color': colour.indigo,
    'border-right-width': '1.5px',
    'border-right-style': 'solid',
    'border-right-color': colour.indigo,
    'border-bottom-width': '1.5px',
    'border-bottom-style': 'solid',
    'border-bottom-color': colour.indigo,
    'border-left-width': '1.5px',
    'border-left-style': 'solid',
    'border-left-color': colour.indigo,
    'border-top-left-radius': radiusScale.pill,
    'border-top-right-radius': radiusScale.pill,
    'border-bottom-left-radius': radiusScale.pill,
    'border-bottom-right-radius': radiusScale.pill,
    'text-decoration-line': 'none',
    'transition-property': 'background-color, color',
    'transition-duration': '150ms',
    'transition-timing-function': 'ease-out',
    cursor: 'pointer',
  },
  variants: {
    hover: { 'background-color': colour.indigo, color: colour.white },
  },
};

/** Reassurance line under a CTA ("No account needed", "Free to start"). */
export const lpCtaNote: StyleDef = {
  name: 'lp-cta-note',
  base: {
    ...TEXT_RESET,
    ...font('small'),
    color: colour.inkMuted,
    'text-align': 'left',
  },
};

/** Wrapper for the embedded conversion button, so it sits correctly in flex rows. */
export const lpCtaEmbed: StyleDef = {
  name: 'lp-cta-embed',
  base: { ...BOX_RESET, display: 'inline-flex', 'align-items': 'center' },
};

const btnCombo = (name: string, base: StyleDef['base']): StyleDef => ({
  name,
  comb: true as const,
  parent: 'lp-cta-secondary',
  base,
});

/**
 * Matches the primary button's sizes, so a secondary next to a primary lines up.
 *
 * These mirror `SIZES` in `blocks/shared/ctaEmbed.ts` exactly. The primary button is an
 * inline-styled embed (its analytics contract requires raw markup) and the secondary is a
 * real class, so the same five steps have to be written twice — `button-sizes.test.ts` asserts
 * the two tables stay in step rather than trusting anyone to remember.
 *
 * `medium` is the base class itself, so it has no combo.
 */
export const ctaSizeCombos: StyleDef[] = [
  btnCombo('is-btn-xsmall', {
    'font-size': '0.8125rem',
    'line-height': '1.125rem',
    'padding-top': '6px',
    'padding-bottom': '6px',
    'padding-left': '16px',
    'padding-right': '16px',
  }),
  btnCombo('is-btn-small', {
    'font-size': '0.875rem',
    'line-height': '1.25rem',
    'padding-top': '8px',
    'padding-bottom': '8px',
    'padding-left': '20px',
    'padding-right': '20px',
  }),
  btnCombo('is-btn-large', {
    'font-size': '1.125rem',
    'line-height': '1.75rem',
    'padding-top': '16px',
    'padding-bottom': '16px',
    'padding-left': '40px',
    'padding-right': '40px',
  }),
  btnCombo('is-btn-xlarge', {
    'font-size': '1.25rem',
    'line-height': '1.875rem',
    'padding-top': '20px',
    'padding-bottom': '20px',
    'padding-left': '48px',
    'padding-right': '48px',
  }),
];

/** Corner style. `pill` is the base, so only the two departures need a combo. */
export const ctaRadiusCombos: StyleDef[] = [
  btnCombo('is-btn-rounded', {
    'border-top-left-radius': radiusScale.md,
    'border-top-right-radius': radiusScale.md,
    'border-bottom-left-radius': radiusScale.md,
    'border-bottom-right-radius': radiusScale.md,
  }),
  btnCombo('is-btn-square', {
    'border-top-left-radius': '0px',
    'border-top-right-radius': '0px',
    'border-bottom-left-radius': '0px',
    'border-bottom-right-radius': '0px',
  }),
];

/**
 * Full-bleed button.
 *
 * `display: flex` rather than `inline-flex` plus centred content: an inline-flex element with
 * `width: 100%` still sits on a text baseline and picks up the parent's `text-align`, which
 * makes a "full width" button look subtly off-centre.
 */
export const ctaFullWidthCombo: StyleDef = btnCombo('is-btn-full', {
  display: 'flex',
  width: '100%',
  'justify-content': 'center',
  'text-align': 'center',
});

/** The embed wrapper has to stop being inline before its button can fill the row. */
export const ctaEmbedFullCombo: StyleDef = {
  name: 'is-cta-embed-full',
  comb: true as const,
  parent: 'lp-cta-embed',
  base: { display: 'block', width: '100%' },
};

export const ctaStyles: StyleDef[] = [
  ...ctaSizeCombos,
  ...ctaRadiusCombos,
  ctaFullWidthCombo,
  ctaEmbedFullCombo,
  lpCtaSecondary,
  lpCtaNote,
  lpCtaEmbed,
];
